<template>
	<form class="md:max-w-80 w-full" @submit.prevent="onSubmit">
		<slot name="csrf" />
		<div class="relative">
			<label for="email-subscribe" class="mb-2 text-white sr-only">Email</label>
			<svg-vue icon="envelope" class="h-3 fill-white pointer-events-none absolute top-1/2 -translate-y-1/2" />
			<input
				type="email"
				name="email"
				id="email-subscribe"
				placeholder="Enter your email"
				class="bg-transparent !ps-6 text-white !border !border-x-0 !border-t-0 !border-b border-white focus:!ring-0 focus:!border-b-white placeholder:text-white"
				v-model="email"
				autocomplete="email"
				required
			/>
		</div>
		<label for="phone-subscribe" class="mb-2 text-white sr-only">Phone</label>
		<div class="relative">
			<svg-vue icon="phone" class="h-3 fill-white pointer-events-none absolute top-1/2 -translate-y-1/2" />
			<input
				type="tel"
				id="phone-subscribe"
				name="phone"
				v-model="phone"
				required
				v-mask="phoneMask"
				placeholder="Enter you phone"
				autocomplete="tel"
				class="bg-transparent !ps-6 text-white !border !border-x-0 !border-t-0 !border-b border-white focus:!ring-0 focus:!border-b-white placeholder:text-white"
			/>

			<button
				type="submit"
				:class="[
					'!absolute right-0 top-1/2 -translate-y-1/2 transition-all ease-out duration-400',
					!email || !phone ? 'opacity-0' : 'opacity-100',
				]"
				:disabled="loading"
				@click.prevent="onSubmit"
			>
				<svg-vue v-if="loading" icon="spinner-third" class="h-3 fill-white animate-spin" />
				<svg-vue v-else icon="arrow-right" class="h-3 fill-white" />
			</button>
		</div>
		<p v-if="success" class="text-xs text-green-200 text-center pt-2">{{ success }}</p>
		<p v-if="error" class="text-xs text-red-400 text-center pt-2">{{ error }}</p>

		<p v-if="currentMarketKey === 'US'" class="text-white !mt-7 text-xs font-normal">
			By submitting this form, you agree to receive recurring automated marketing text messages (e.g. AI content, cart
			reminders) from SEINT at the number you provide. Consent not a condition of purchase. We may share info with
			service providers per our Privacy Policy. Reply HELP for help & STOP to cancel. Msg frequency varies. Msg & data
			rates may apply. By clicking this button, you also agree to our
			<a href="https://attnl.tv/t/qn3/r/US" target="_blank" class="text-white underline">Terms</a> (incl. arbitration) &
			<a href="https://attnl.tv/p/qn3" target="_blank" class="text-white underline">Privacy Policy</a>.
		</p>
		<p v-else-if="currentMarketKey === 'CA'" class="text-white !mt-7 text-xs font-normal">
			By submitting this form, you agree to receive marketing text messages (e.g. AI content, cart reminders) from SEINT
			at the number you provide. Reply HELP for help. You can withdraw your consent at any time by texting STOP. Msg
			frequency varies. Std Msg & data rates may apply. By submitting this form, you also agree to the
			<a href="https://attnl.tv/t/qn3/r/US" target="_blank" class="text-white underline">Terms</a> and
			<a href="https://attnl.tv/p/qn3" target="_blank" class="text-white underline">Privacy</a>. Contact SEINT at 84
			West 1470 South Ste 1 St George, UT 84770 + ask@seintofficial.com
		</p>
		<p v-else-if="['UK', 'GB'].includes(currentMarketKey)" class="text-white text-xs !mt-7 font-normal">
			By submitting this form, you agree to receive marketing text messages including AI content from SEINT and to the
			Terms. We may use information collected about you on our site to suggest other products and offers based on your
			profile and in accordance with our Privacy Policy. You can unsubscribe at any time by replying STOP to our text.
			<a href="https://attnl.tv/t/qn3/r/US" target="_blank" class="text-white underline">View Terms</a> and
			<a href="https://attnl.tv/p/qn3" target="_blank" class="text-white underline">Privacy</a> for more information on
			the processing of your data and your privacy rights.
		</p>
	</form>
</template>
<script setup>
import { ref, computed } from 'vue'
import axios from 'axios'

const props = defineProps({
	endpoint: {
		type: String,
		required: true,
	},
	currentMarketKey: {
		type: String,
		default: 'US',
	},
	groupName: {
		type: String,
		default: 'Master List',
	},
})

const { endpoint, groupName } = props
const loading = ref(false)
const success = ref(null)
const error = ref(null)
const email = ref(null)
const phone = ref(null)

const phoneMask = computed(() => {
	if (props.currentMarketKey === 'US') {
		return ['+1 ###-###-####']
	}
	if (props.currentMarketKey === 'CA') {
		return ['+1 ###-###-####']
	}
	if (['UK', 'GB'].includes(props.currentMarketKey)) {
		return ['+44 ## #### ####']
	}
})

const signupsourceID = computed(() => {
	if (props.currentMarketKey === 'US') {
		return 1155858
	}
	if (props.currentMarketKey === 'CA') {
		return 1155864
	}
	if (['UK', 'GB'].includes(props.currentMarketKey)) {
		return 1155863
	}
})

const onSubmit = async () => {
	try {
		loading.value = true
		success.value = null
		error.value = null
		const payload = {
			email: email.value,
			phone: phone.value,
			groupName,
			signupsourceID: signupsourceID.value,
		}
		await axios.post(endpoint, payload)
		success.value = 'Successfully Subscribed!'
		loading.value = false
	} catch (err) {
		loading.value = false
		error.value = err.response?.data.message ?? 'An error occured'
	}
}
</script>
<style scoped>
input:-webkit-autofill {
	-webkit-box-shadow: 0 0 0 1000px rgb(0, 0, 0) inset !important;
	-webkit-text-fill-color: white !important;
}
</style>
